/**
 * Takvim bileşenleri (`ui/calendar.tsx`, `filters/DateRangePicker.tsx`) yerel gece yarısı `Date` ile çalışır;
 * adres çubuğu ise `YYYY-MM-DD` takvim günü taşır. Dönüşüm yerel yıl/ay/gün alanlarıyla yapılır:
 * `toISOString`/`new Date('YYYY-MM-DD')` UTC'ye göre okuyup günü kaydırabilirdi (AGENTS.md §5).
 */
const pad = (value: number) => String(value).padStart(2, '0')

export function dateInputToLocalDate(value: string | undefined): Date | undefined {
  const match = value && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return undefined
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export function localDateToDateInput(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** `YYYY-MM-DD` anahtarı; `localDateToDateInput` ile aynı. */
export const dateKey = localDateToDateInput

/**
 * Ortak gün aritmetiği (uiarc calendar / date-range-picker). Hepsi yerel yıl/ay/gün alanlarıyla çalışır;
 * Cube metinleri buraya `new Date(...)` ile girmez.
 */
const DAY_MS = 864e5

export const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())
export const monthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)
export const addDays = (date: Date, amount: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
export const addMonths = (date: Date, amount: number) =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1)
/** Tam ay kaydırır, günü korur; kısa ayda ayın son gününe sıkıştırır (31 Ocak + 1 ay = 28 Şubat). */
export const shiftMonths = (date: Date, amount: number) =>
  new Date(
    date.getFullYear(),
    date.getMonth() + amount,
    Math.min(
      date.getDate(),
      new Date(date.getFullYear(), date.getMonth() + amount + 1, 0).getDate(),
    ),
  )
/** `a - b` gün sayısı (yaz saati kaymasına karşı yuvarlanır). */
export const dayDiff = (a: Date, b: Date) =>
  Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / DAY_MS)
export const monthDiff = (a: Date, b: Date) =>
  (a.getFullYear() - b.getFullYear()) * 12 + a.getMonth() - b.getMonth()
export const sameDay = (a?: Date | null, b?: Date | null) => Boolean(a && b && dayDiff(a, b) === 0)
export const sameMonth = (a?: Date | null, b?: Date | null) =>
  Boolean(a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth())
